import React, { useState, useEffect } from 'react';
import { customerAPI } from '../services/api';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    company_name:'', contact_person:'',
    email:'', phone:'', alternate_phone:'',
    gst_number:'', billing_address:'',
    city:'', state:'', pincode:'', notes:'',
  });

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    company_name:'', contact_person:'',
    email:'', phone:'', alternate_phone:'',
    gst_number:'', billing_address:'',
    city:'', state:'', pincode:'', notes:'',
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete state
  const [deleting, setDeleting] = useState(false);

  // Address state
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [addressForm, setAddressForm] = useState({
    label:'', contact_person:'', phone:'',
    address:'', city:'', state:'', pincode:'',
    is_default:false,
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [editingAddressId, setEditingAddressId] = useState(null);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      const res = await customerAPI.getAll(params);
      setCustomers(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const res = await customerAPI.getOne(id);
      setDetail(res.data.data);
    } catch (err) {
      console.error('Failed:', err);
    }
  };

  const handleAdd = async () => {
    if (!form.company_name || !form.phone) {
      setError(
        'Company name and phone are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await customerAPI.add(form);
      setForm({
        company_name:'', contact_person:'',
        email:'', phone:'', alternate_phone:'',
        gst_number:'', billing_address:'',
        city:'', state:'', pincode:'', notes:'',
      });
      setShowAdd(false);
      await loadCustomers();
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'Failed to add customer');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = () => {
    setEditForm({
      company_name: detail.customer.company_name || '',
      contact_person: detail.customer.contact_person || '',
      email: detail.customer.email || '',
      phone: detail.customer.phone || '',
      alternate_phone: detail.customer.alternate_phone || '',
      gst_number: detail.customer.gst_number || '',
      billing_address: detail.customer.billing_address || '',
      city: detail.customer.city || '',
      state: detail.customer.state || '',
      pincode: detail.customer.pincode || '',
      notes: detail.customer.notes || '',
    });
    setEditError('');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editForm.company_name) {
      setEditError('Company name is required');
      return;
    }
    if (!editForm.phone) {
      setEditError('Phone is required');
      return;
    }
    setSavingEdit(true);
    setEditError('');
    try {
      await customerAPI.update(selected.id, editForm);
      await loadDetail(selected.id);
      setEditing(false);
      await loadCustomers();
    } catch (err) {
      setEditError(
        err.response?.data?.message
        || 'Failed to update customer');
    } finally {
      setSavingEdit(false);
    }
  };
    const resetAddressForm = () => setAddressForm({
    label:'', contact_person:'', phone:'',
    address:'', city:'', state:'', pincode:'',
    is_default:false,
  });

  const handleSaveAddress = async () => {
    if (!addressForm.address) {
      setAddressError('Address is required');
      return;
    }
    setSavingAddress(true);
    setAddressError('');
    try {
      if (editingAddressId) {
        await customerAPI.updateAddress(
          editingAddressId, addressForm);
      } else {
        await customerAPI.addAddress(
          selected.id, addressForm);
      }
      resetAddressForm();
      setShowAddAddress(false);
      setEditingAddressId(null);
      await loadDetail(selected.id);
    } catch (err) {
      setAddressError(
        err.response?.data?.message
        || 'Failed to save address');
    } finally {
      setSavingAddress(false);
    }
  };

  const openEditAddress = (a) => {
    setAddressForm({
      label: a.label || '',
      contact_person: a.contact_person || '',
      phone: a.phone || '',
      address: a.address || '',
      city: a.city || '',
      state: a.state || '',
      pincode: a.pincode || '',
      is_default: a.is_default || false,
    });
    setEditingAddressId(a.id);
    setShowAddAddress(true);
    setAddressError('');
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm(
      'Delete this site address?')) return;
    try {
      await customerAPI.deleteAddress(addressId);
      await loadDetail(selected.id);
    } catch (err) {
      alert(
        err.response?.data?.message
        || 'Failed to delete address');
    }
  };
  const handleDeleteCustomer = async (id) => {
    if (!window.confirm(
      'Delete this customer? Their order history ' +
      'will be preserved, but they will no longer ' +
      'appear in your customer list. This cannot be ' +
      'undone from the dashboard.'
    )) return;
    setDeleting(true);
    try {
      await customerAPI.delete(id);
      await loadCustomers();
      setSelected(null);
      setDetail(null);
    } catch (err) {
      alert(
        err.response?.data?.message
        || 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const inputStyle = {
    width:'100%',
    background:'var(--surface2)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'10px 14px',
    borderRadius:'8px',
    fontSize:'13.5px',
    outline:'none',
  };

  const labelStyle = {
    display:'block',
    fontSize:'11px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'1px',
    textTransform:'uppercase',
    marginBottom:'6px',
  };

  const smallInputStyle = {
    width:'100%',
    background:'var(--surface)',
    border:'1px solid var(--border2)',
    color:'var(--text)',
    padding:'8px 10px',
    borderRadius:'6px',
    fontSize:'12.5px',
    outline:'none',
    marginBottom:'10px',
  };

  const smallLabelStyle = {
    display:'block',
    fontSize:'10px',
    fontFamily:'DM Mono,monospace',
    color:'var(--text3)',
    letterSpacing:'0.5px',
    textTransform:'uppercase',
    marginBottom:'4px',
  };

  return (
    <div style={{ padding:'24px' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'center',
        marginBottom:'24px',
      }}>
        <div>
          <h1 style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'22px', fontWeight:'800',
          }}>
            Customers
          </h1>
          <p style={{
            color:'var(--text3)',
            fontSize:'13px', marginTop:'4px',
          }}>
            Manage your customer directory
          </p>
        </div>
        <button
          onClick={() => setShowAdd(!showAdd)}
          style={{
            background:'var(--accent)',
            color:'var(--bg)',
            border:'none',
            padding:'10px 20px',
            borderRadius:'8px',
            fontSize:'13px',
            fontWeight:'600',
            cursor:'pointer',
          }}
        >
          + Add Customer
        </button>
      </div>

      {/* Add Customer Form */}
      {showAdd && (
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          padding:'24px',
          marginBottom:'20px',
        }}>
          <div style={{
            fontFamily:'Syne,sans-serif',
            fontSize:'15px', fontWeight:'700',
            marginBottom:'20px',
          }}>
            Add New Customer
          </div>
          <div style={{
            display:'grid',
            gridTemplateColumns:'1fr 1fr',
            gap:'16px',
          }}>
            <div>
              <label style={labelStyle}>
                Company Name *
              </label>
              <input style={inputStyle}
                placeholder="Company or person name"
                value={form.company_name}
                onChange={e => setForm({
                  ...form,
                  company_name:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Contact Person
              </label>
              <input style={inputStyle}
                placeholder="Contact person name"
                value={form.contact_person}
                onChange={e => setForm({
                  ...form,
                  contact_person:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Phone *
              </label>
              <input style={inputStyle}
                placeholder="Primary phone"
                value={form.phone}
                onChange={e => setForm({
                  ...form, phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Alternate Phone
              </label>
              <input style={inputStyle}
                placeholder="Alternate phone"
                value={form.alternate_phone}
                onChange={e => setForm({
                  ...form,
                  alternate_phone:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Email
              </label>
              <input style={inputStyle}
                placeholder="Email address"
                value={form.email}
                onChange={e => setForm({
                  ...form, email:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                GST Number
              </label>
              <input style={inputStyle}
                placeholder="GST (optional)"
                value={form.gst_number}
                onChange={e => setForm({
                  ...form,
                  gst_number:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                City
              </label>
              <input style={inputStyle}
                placeholder="City"
                value={form.city}
                onChange={e => setForm({
                  ...form, city:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                State
              </label>
              <input style={inputStyle}
                placeholder="State"
                value={form.state}
                onChange={e => setForm({
                  ...form, state:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Pincode
              </label>
              <input style={inputStyle}
                placeholder="Pincode"
                value={form.pincode}
                onChange={e => setForm({
                  ...form,
                  pincode:e.target.value
                })}
              />
            </div>
            <div>
              <label style={labelStyle}>
                Notes
              </label>
              <input style={inputStyle}
                placeholder="Any notes"
                value={form.notes}
                onChange={e => setForm({
                  ...form, notes:e.target.value
                })}
              />
            </div>
            <div style={{
              gridColumn:'1 / -1',
            }}>
              <label style={labelStyle}>
                Billing Address
              </label>
              <textarea
                style={{
                  ...inputStyle,
                  minHeight:'70px',
                  resize:'vertical',
                }}
                placeholder="Full billing address"
                value={form.billing_address}
                onChange={e => setForm({
                  ...form,
                  billing_address:e.target.value
                })}
              />
            </div>
          </div>

          {error && (
            <div style={{
              background:
                'rgba(255,77,109,0.1)',
              border:
                '1px solid rgba(255,77,109,0.3)',
              color:'var(--red)',
              padding:'10px 14px',
              borderRadius:'8px',
              fontSize:'13px',
              marginTop:'16px',
            }}>
              {error}
            </div>
          )}

          <div style={{
            display:'flex', gap:'10px',
            marginTop:'20px',
          }}>
            <button onClick={handleAdd}
              disabled={saving}
              style={{
                background:'var(--accent)',
                color:'var(--bg)',
                border:'none',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                fontWeight:'600',
                cursor: saving
                  ? 'not-allowed' : 'pointer',
              }}
            >
              {saving
                ? 'Saving...' : 'Save Customer'}
            </button>
            <button
              onClick={() => {
                setShowAdd(false);
                setError('');
              }}
              style={{
                background:'transparent',
                color:'var(--text2)',
                border:
                  '1px solid var(--border2)',
                padding:'10px 24px',
                borderRadius:'8px',
                fontSize:'13px',
                cursor:'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{
        display:'flex', gap:'10px',
        marginBottom:'16px',
      }}>
        <div style={{
          display:'flex',
          alignItems:'center',
          background:'var(--surface2)',
          border:'1px solid var(--border2)',
          borderRadius:'8px',
          padding:'0 12px',
          gap:'8px', flex:1,
          maxWidth:'300px',
        }}>
          <span style={{
            color:'var(--text3)',
          }}>⊞</span>
          <input
            placeholder="Search customers..."
            value={search}
            onChange={e =>
              setSearch(e.target.value)}
            onKeyPress={e =>
              e.key === 'Enter' &&
              loadCustomers()}
            style={{
              background:'none',
              border:'none', outline:'none',
              color:'var(--text)',
              fontSize:'13px',
              padding:'9px 0', flex:1,
            }}
          />
        </div>
        <button onClick={loadCustomers}
          style={{
            background:'var(--surface2)',
            border:'1px solid var(--border2)',
            color:'var(--text2)',
            padding:'8px 16px',
            borderRadius:'8px',
            fontSize:'13px',
            cursor:'pointer',
          }}
        >
          Search
        </button>
      </div>

      {/* Main Layout */}
      <div style={{
        display:'grid',
        gridTemplateColumns:
          selected ? '1fr 360px' : '1fr',
        gap:'16px',
      }}>
        {/* Customers Table */}
        <div style={{
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'12px',
          overflow:'hidden',
        }}>
          {loading ? (
            <div style={{
              padding:'40px',
              textAlign:'center',
              color:'var(--text3)',
              fontFamily:'DM Mono,monospace',
            }}>
              Loading customers...
            </div>
          ) : customers.length === 0 ? (
            <div style={{
              padding:'60px',
              textAlign:'center',
              color:'var(--text3)',
            }}>
              <div style={{
                fontSize:'40px',
                marginBottom:'12px',
              }}>◫</div>
              <div style={{
                fontSize:'14px',
                color:'var(--text2)',
                marginBottom:'6px',
              }}>
                No customers yet
              </div>
              <div style={{ fontSize:'12px' }}>
                Click "+ Add Customer"
                to add your first customer
              </div>
            </div>
          ) : (
            <table style={{
              width:'100%',
              borderCollapse:'collapse',
            }}>
              <thead>
                <tr>
                  {[
                    'Company','Contact',
                    'Phone','City',
                    'Total Orders',
                    'Active','Actions'
                  ].map(h => (
                    <th key={h} style={{
                      padding:'10px 16px',
                      textAlign:'left',
                      fontSize:'10px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text3)',
                      letterSpacing:'1.5px',
                      textTransform:'uppercase',
                      borderBottom:
                        '1px solid var(--border)',
                      background:
                        'var(--surface2)',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {customers.map(c => (
                  <tr key={c.id}
                    onClick={() => {
                      setSelected(c);
                      loadDetail(c.id);
                      setEditing(false);
                    }}
                    style={{
                      borderBottom:
                        '1px solid var(--border)',
                      cursor:'pointer',
                      background:
                        selected?.id === c.id
                          ? 'rgba(0,212,255,0.05)'
                          : 'transparent',
                    }}
                  >
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <div style={{
                        fontWeight:'600',
                        fontSize:'13px',
                      }}>
                        {c.company_name}
                      </div>
                      {c.gst_number && (
                        <div style={{
                          fontSize:'10px',
                          color:'var(--text3)',
                          fontFamily:
                            'DM Mono,monospace',
                          marginTop:'2px',
                        }}>
                          GST: {c.gst_number}
                        </div>
                      )}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      color:'var(--text2)',
                    }}>
                      {c.contact_person || '—'}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'12px',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--text2)',
                    }}>
                      {c.phone}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      fontSize:'13px',
                      color:'var(--text2)',
                    }}>
                      {c.city || '—'}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      textAlign:'center',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--accent)',
                    }}>
                      {c.total_tickets || 0}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                      textAlign:'center',
                      fontFamily:
                        'DM Mono,monospace',
                      color:'var(--yellow)',
                    }}>
                      {c.active_tickets || 0}
                    </td>
                    <td style={{
                      padding:'12px 16px',
                    }}>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setSelected(c);
                          loadDetail(c.id);
                        }}
                        style={{
                          background:
                            'rgba(0,212,255,0.1)',
                          color:'var(--accent)',
                          border:
                            '1px solid rgba(0,212,255,0.2)',
                          padding:'4px 12px',
                          borderRadius:'6px',
                          fontSize:'12px',
                          cursor:'pointer',
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail Panel */}
        {selected && detail && (
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border)',
            borderRadius:'12px',
            overflow:'hidden',
            height:'fit-content',
          }}>
            <div style={{
              padding:'16px 20px',
              borderBottom:
                '1px solid var(--border)',
              display:'flex',
              justifyContent:'space-between',
              alignItems:'center',
            }}>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'14px',
                fontWeight:'700',
              }}>
                Customer Details
              </div>
              <div style={{
                display:'flex',
                alignItems:'center',
                gap:'12px',
              }}>
                {!editing && (
                  <button
                    onClick={openEdit}
                    style={{
                      background:'none',
                      border:'none',
                      color:'var(--accent)',
                      cursor:'pointer',
                      fontSize:'12px',
                      fontWeight:'600',
                    }}
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={() => {
                    setSelected(null);
                    setDetail(null);
                    setEditing(false);
                  }}
                  style={{
                    background:'none',
                    border:'none',
                    color:'var(--text3)',
                    cursor:'pointer',
                    fontSize:'18px',
                  }}
                >
                  ✕
                </button>
              </div>
            </div>

            <div style={{ padding:'20px' }}>
              {editing ? (
                <>
                  <div>
                    <label style={smallLabelStyle}>
                      Company Name *
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.company_name}
                      onChange={e => setEditForm({
                        ...editForm,
                        company_name:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Contact Person
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.contact_person}
                      onChange={e => setEditForm({
                        ...editForm,
                        contact_person:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Phone *
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.phone}
                      onChange={e => setEditForm({
                        ...editForm,
                        phone:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Alternate Phone
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.alternate_phone}
                      onChange={e => setEditForm({
                        ...editForm,
                        alternate_phone:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Email
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.email}
                      onChange={e => setEditForm({
                        ...editForm,
                        email:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      GST Number
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.gst_number}
                      onChange={e => setEditForm({
                        ...editForm,
                        gst_number:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      City
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.city}
                      onChange={e => setEditForm({
                        ...editForm,
                        city:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      State
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.state}
                      onChange={e => setEditForm({
                        ...editForm,
                        state:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Pincode
                    </label>
                    <input
                      style={smallInputStyle}
                      value={editForm.pincode}
                      onChange={e => setEditForm({
                        ...editForm,
                        pincode:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Billing Address
                    </label>
                    <textarea
                      style={{
                        ...smallInputStyle,
                        minHeight:'60px',
                        resize:'vertical',
                      }}
                      value={editForm.billing_address}
                      onChange={e => setEditForm({
                        ...editForm,
                        billing_address:e.target.value
                      })}
                    />
                  </div>
                  <div>
                    <label style={smallLabelStyle}>
                      Notes
                    </label>
                    <textarea
                      style={{
                        ...smallInputStyle,
                        minHeight:'50px',
                        resize:'vertical',
                      }}
                      value={editForm.notes}
                      onChange={e => setEditForm({
                        ...editForm,
                        notes:e.target.value
                      })}
                    />
                  </div>

                  {editError && (
                    <div style={{
                      background:
                        'rgba(255,77,109,0.1)',
                      border:
                        '1px solid rgba(255,77,109,0.3)',
                      color:'var(--red)',
                      padding:'8px 10px',
                      borderRadius:'6px',
                      fontSize:'12px',
                      marginBottom:'10px',
                    }}>
                      {editError}
                    </div>
                  )}

                  <div style={{
                    display:'flex',
                    gap:'8px',
                  }}>
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      style={{
                        flex:1,
                        background:'var(--accent)',
                        color:'var(--bg)',
                        border:'none',
                        padding:'9px',
                        borderRadius:'6px',
                        fontSize:'12.5px',
                        fontWeight:'600',
                        cursor: savingEdit
                          ? 'not-allowed'
                          : 'pointer',
                      }}
                    >
                      {savingEdit
                        ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => {
                        setEditing(false);
                        setEditError('');
                      }}
                      style={{
                        flex:1,
                        background:'transparent',
                        color:'var(--text2)',
                        border:
                          '1px solid var(--border2)',
                        padding:'9px',
                        borderRadius:'6px',
                        fontSize:'12.5px',
                        cursor:'pointer',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
              <>
              <div style={{
                fontFamily:'Syne,sans-serif',
                fontSize:'17px',
                fontWeight:'700',
                marginBottom:'4px',
              }}>
                {detail.customer.company_name}
              </div>
              {detail.customer.contact_person && (
                <div style={{
                  fontSize:'13px',
                  color:'var(--text3)',
                  marginBottom:'16px',
                }}>
                  {detail.customer.contact_person}
                </div>
              )}

              {[
                ['Phone',
                  detail.customer.phone],
                ['Alt Phone',
                  detail.customer.alternate_phone
                  || '—'],
                ['Email',
                  detail.customer.email || '—'],
                ['GST',
                  detail.customer.gst_number
                  || '—'],
                ['City', detail.customer.city
                  || '—'],
                ['State',
                  detail.customer.state || '—'],
                ['Pincode',
                  detail.customer.pincode || '—'],
              ].map(([label, value]) => (
                <div key={label} style={{
                  display:'flex',
                  justifyContent:'space-between',
                  padding:'8px 0',
                  borderBottom:
                    '1px solid var(--border)',
                  fontSize:'12.5px',
                }}>
                  <span style={{
                    color:'var(--text3)',
                    fontFamily:
                      'DM Mono,monospace',
                    fontSize:'11px',
                  }}>
                    {label}
                  </span>
                  <span style={{
                    color:'var(--text)',
                    fontWeight:'500',
                  }}>
                    {value}
                  </span>
                </div>
              ))}

              {detail.customer.billing_address && (
                <div style={{
                  marginTop:'12px',
                  padding:'12px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                  fontSize:'12.5px',
                  color:'var(--text2)',
                }}>
                  <div style={{
                    fontSize:'10px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    marginBottom:'6px',
                    letterSpacing:'1px',
                  }}>
                    BILLING ADDRESS
                  </div>
                  {detail.customer
                    .billing_address}
                </div>
              )}
			  {/* Site Addresses */}
              <div style={{ marginTop:'16px' }}>
                <div style={{
                  display:'flex',
                  justifyContent:'space-between',
                  alignItems:'center',
                  marginBottom:'8px',
                }}>
                  <div style={{
                    fontSize:'10px',
                    fontFamily:'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                  }}>
                    SITE ADDRESSES
                  </div>
                  <button
                    onClick={() => {
                      resetAddressForm();
                      setEditingAddressId(null);
                      setShowAddAddress(
                        !showAddAddress);
                      setAddressError('');
                    }}
                    style={{
                      background:'none',
                      border:'none',
                      color:'var(--accent)',
                      cursor:'pointer',
                      fontSize:'11px',
                      fontWeight:'600',
                    }}
                  >
                    {showAddAddress
                      ? 'Cancel' : '+ Add Site'}
                  </button>
                </div>

                {showAddAddress && (
                  <div style={{
                    background:'var(--surface2)',
                    borderRadius:'8px',
                    padding:'12px',
                    marginBottom:'12px',
                  }}>
                    <label style={smallLabelStyle}>
                      Label (e.g. Malad Branch)
                    </label>
                    <input style={smallInputStyle}
                      value={addressForm.label}
                      onChange={e => setAddressForm({
                        ...addressForm,
                        label:e.target.value
                      })}
                    />
                    <label style={smallLabelStyle}>
                      Address *
                    </label>
                    <textarea style={{
                      ...smallInputStyle,
                      minHeight:'50px',
                      resize:'vertical',
                    }}
                      value={addressForm.address}
                      onChange={e => setAddressForm({
                        ...addressForm,
                        address:e.target.value
                      })}
                    />
                    <div style={{
                      display:'grid',
                      gridTemplateColumns:'1fr 1fr',
                      gap:'8px',
                    }}>
                      <div>
                        <label style={smallLabelStyle}>
                          City
                        </label>
                        <input style={smallInputStyle}
                          value={addressForm.city}
                          onChange={e => setAddressForm({
                            ...addressForm,
                            city:e.target.value
                          })}
                        />
                      </div>
                      <div>
                        <label style={smallLabelStyle}>
                          Pincode
                        </label>
                        <input style={smallInputStyle}
                          value={addressForm.pincode}
                          onChange={e => setAddressForm({
                            ...addressForm,
                            pincode:e.target.value
                          })}
                        />
                      </div>
                    </div>
                    <label style={smallLabelStyle}>
                      Site Contact Person
                    </label>
                    <input style={smallInputStyle}
                      value={addressForm.contact_person}
                      onChange={e => setAddressForm({
                        ...addressForm,
                        contact_person:e.target.value
                      })}
                    />
                    <label style={smallLabelStyle}>
                      Site Contact Phone
                    </label>
                    <input style={smallInputStyle}
                      value={addressForm.phone}
                      onChange={e => setAddressForm({
                        ...addressForm,
                        phone:e.target.value
                      })}
                    />
                    <label style={{
                      display:'flex',
                      alignItems:'center',
                      gap:'6px',
                      fontSize:'12px',
                      color:'var(--text2)',
                      marginBottom:'10px',
                      cursor:'pointer',
                    }}>
                      <input type="checkbox"
                        checked={addressForm.is_default}
                        onChange={e => setAddressForm({
                          ...addressForm,
                          is_default:e.target.checked
                        })}
                      />
                      Set as default address
                    </label>

                    {addressError && (
                      <div style={{
                        background:
                          'rgba(255,77,109,0.1)',
                        border:
                          '1px solid rgba(255,77,109,0.3)',
                        color:'var(--red)',
                        padding:'6px 10px',
                        borderRadius:'6px',
                        fontSize:'11.5px',
                        marginBottom:'8px',
                      }}>
                        {addressError}
                      </div>
                    )}

                    <button
                      onClick={handleSaveAddress}
                      disabled={savingAddress}
                      style={{
                        width:'100%',
                        background:'var(--accent)',
                        color:'var(--bg)',
                        border:'none',
                        padding:'8px',
                        borderRadius:'6px',
                        fontSize:'12px',
                        fontWeight:'600',
                        cursor: savingAddress
                          ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {savingAddress
                        ? 'Saving...'
                        : editingAddressId
                          ? 'Update Address'
                          : 'Save Address'}
                    </button>
                  </div>
                )}

                {detail.addresses?.length > 0 ? (
                  detail.addresses.map(a => (
                    <div key={a.id} style={{
                      background:'var(--surface2)',
                      borderRadius:'8px',
                      padding:'10px 12px',
                      marginBottom:'8px',
                      fontSize:'12.5px',
                    }}>
                      <div style={{
                        display:'flex',
                        justifyContent:
                          'space-between',
                        alignItems:'center',
                      }}>
                        <div style={{
                          fontWeight:'600',
                          color:'var(--text)',
                        }}>
                          {a.label || 'Address'}
                          {a.is_default && (
                            <span style={{
                              marginLeft:'6px',
                              fontSize:'9px',
                              background:
                                'rgba(0,229,160,0.15)',
                              color:'var(--green)',
                              padding:'1px 6px',
                              borderRadius:'8px',
                            }}>
                              DEFAULT
                            </span>
                          )}
                        </div>
                        <div style={{
                          display:'flex', gap:'8px',
                        }}>
                          <button
                            onClick={() =>
                              openEditAddress(a)}
                            style={{
                              background:'none',
                              border:'none',
                              color:'var(--accent)',
                              fontSize:'11px',
                              cursor:'pointer',
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteAddress(a.id)}
                            style={{
                              background:'none',
                              border:'none',
                              color:'var(--red)',
                              fontSize:'11px',
                              cursor:'pointer',
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div style={{
                        color:'var(--text2)',
                        marginTop:'4px',
                      }}>
                        {a.address}
                        {a.city ? `, ${a.city}` : ''}
                        {a.pincode
                          ? ` - ${a.pincode}` : ''}
                      </div>
                      {a.contact_person && (
                        <div style={{
                          color:'var(--text3)',
                          fontSize:'11px',
                          marginTop:'2px',
                        }}>
                          {a.contact_person}
                          {a.phone
                            ? ` · ${a.phone}` : ''}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  !showAddAddress && (
                    <div style={{
                      color:'var(--text3)',
                      fontSize:'12px',
                    }}>
                      No site addresses added.
                      Billing address will be used
                      for tickets.
                    </div>
                  )
                )}
              </div>

              {/* Ticket History */}
              {detail.ticket_history?.length > 0 && (
                <div style={{
                  marginTop:'16px',
                }}>
                  <div style={{
                    fontSize:'11px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    letterSpacing:'1px',
                    marginBottom:'8px',
                  }}>
                    RECENT TICKETS
                  </div>
                  {detail.ticket_history
                    .slice(0,5).map(t => (
                    <div key={t.id} style={{
                      display:'flex',
                      justifyContent:
                        'space-between',
                      alignItems:'center',
                      padding:'8px 0',
                      borderBottom:
                        '1px solid var(--border)',
                      fontSize:'12px',
                    }}>
                      <div>
                        <span style={{
                          color:'var(--accent)',
                          fontFamily:
                            'DM Mono,monospace',
                        }}>
                          {t.ticket_number}
                        </span>
                        <span style={{
                          color:'var(--text3)',
                          marginLeft:'8px',
                        }}>
                          {t.ticket_type}
                        </span>
                      </div>
                      <span style={{
                        fontSize:'10px',
                        padding:'2px 8px',
                        borderRadius:'10px',
                        background:
                          t.status === 'closed'
                            ? 'rgba(77,95,122,0.2)'
                          : t.status ===
                            'delivered'
                            ? 'rgba(0,229,160,0.1)'
                          : 'rgba(255,192,64,0.1)',
                        color:
                          t.status === 'closed'
                            ? 'var(--text3)'
                          : t.status ===
                            'delivered'
                            ? 'var(--green)'
                          : 'var(--yellow)',
                      }}>
                        {t.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {detail.customer.notes && (
                <div style={{
                  marginTop:'12px',
                  padding:'12px',
                  background:'var(--surface2)',
                  borderRadius:'8px',
                  fontSize:'12.5px',
                  color:'var(--text2)',
                }}>
                  <div style={{
                    fontSize:'10px',
                    fontFamily:
                      'DM Mono,monospace',
                    color:'var(--text3)',
                    marginBottom:'6px',
                    letterSpacing:'1px',
                  }}>
                    NOTES
                  </div>
                  {detail.customer.notes}
                </div>
              )}

              <button
                onClick={() =>
                  handleDeleteCustomer(
                    selected.id)}
                disabled={deleting}
                style={{
                  width:'100%',
                  background:
                    'rgba(255,77,109,0.1)',
                  color:'var(--red)',
                  border:
                    '1px solid rgba(255,77,109,0.3)',
                  padding:'10px',
                  borderRadius:'8px',
                  fontSize:'13px',
                  fontWeight:'600',
                  cursor: deleting
                    ? 'not-allowed'
                    : 'pointer',
                  marginTop:'16px',
                }}
              >
                {deleting
                  ? 'Deleting...'
                  : 'Delete Customer'}
              </button>
              </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Customers;